import { useNavigation } from '@react-navigation/native'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FlatList, useWindowDimensions, View } from 'react-native'
import { IconButton, Text } from 'react-native-paper'
import { useAuthContext } from '../../../../../contexts/auth-context'
import Row from '../../../../../@ui/containers/grid/row'
import { Icon } from '../../../../../@ui/typo/icon'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import { useTheme } from '../../../../../hooks/theme.hook'
import { IconFilter, IconPrivate } from '../../../../../utils/meldd-icons'
import { Cluster } from '../types'
import { Article } from '../../../perspectives/perspective/types'
import { UserLink } from '../../../../../@ui/typo/user.link'
import { PerspectiveTile } from './perspective.tile'
import { MelddSearchBar } from '../../../../../@ui/form/meldd-search.input'
import { ContributionType } from '../../../../../generated/graphql'
import { MelddCard } from '../../../../../@ui/containers/cards/meldd.card'
import { useReplayApi } from '../../../../../services/snapshots/replay-api.hook'

interface ClusterCardProps {
  cluster: Cluster
  onRefresh: () => void
  refreshing: boolean
  onPerspectivePress: (perspective: Article) => void
  navigateToContributions: (
    perspectiveId: string,
    contributionType: ContributionType
  ) => void
}

const NoPerspectivesFound = () => {
  const { t } = useTranslation('clusters')

  return (
    <View
      style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Text style={{ textAlign: 'center', fontSize: 12 }}>
        {t('no-perspectives-found')}
      </Text>
    </View>
  )
}

export function ClusterCardReplay(props: ClusterCardProps) {
  const { width } = useWindowDimensions()
  const { t } = useTranslation('clusters')
  const theme = useTheme()
  const { cluster } = props
  const styles = useStyle()
  const [searchInput, setSearchInput] = useState('')
  const [displaySearchBar, setDisplaySearchBar] = useState(false)
  const listRef = useRef<FlatList<Article>>(null)
  const { clusterService } = useReplayApi()

  const articles = clusterService.getClusterPerspectives(props.cluster.id) ?? []

  const filteredArticles = articles.filter((article) => {
    return (
      !searchInput ||
      article.title.toLowerCase().includes(searchInput.toLowerCase())
    )
  })

  return (
    <MelddCard
      header={
        <View style={styles.titleWrapper}>
          <View style={styles.title}>
            <View style={styles.mainTitle}>
              <Text
                variant={width < 480 ? 'titleSmall' : 'titleMedium'}
                numberOfLines={15}
                ellipsizeMode={'tail'}
              >
                {cluster.topic}
              </Text>

              <IconButton
                mode="contained-tonal"
                size={20}
                icon={IconFilter}
                style={styles.button}
                iconColor={displaySearchBar ? theme.colors.primary : undefined}
                onPress={() => setDisplaySearchBar(!displaySearchBar)}
              />
            </View>
            {displaySearchBar && (
              <Row>
                <MelddSearchBar
                  icon={IconFilter}
                  placeholder={t('search-clusters')}
                  onChangeText={setSearchInput}
                  value={searchInput}
                  autoFocus={true}
                  style={{ button: { backgroundColor: 'transparent' } }}
                />
              </Row>
            )}

            <View
              style={[
                styles.subTitle,
                {
                  marginTop:
                    cluster.moderators.length > 0
                      ? theme.spacing(0)
                      : theme.spacing(3),
                },
              ]}
            >
              {cluster.isPrivate ? (
                <>
                  <Icon
                    source={IconPrivate}
                    size={16}
                    color={theme.colors.onSurfaceVariant}
                  />
                  <Text variant={'bodySmall'}>{t('Private')}</Text>
                </>
              ) : (
                <Text variant="bodySmall">{t('Public')}</Text>
              )}
            </View>

            {cluster.moderators.length > 0 && (
              <View style={styles.moderators}>
                <Text variant={'bodySmall'}>{t('Moderators')}:</Text>
                {cluster.moderators.map((moderator, index) => (
                  <UserLink key={index} user={moderator.user} />
                ))}
              </View>
            )}
          </View>
        </View>
      }
    >
      <FlatList
        ref={listRef}
        data={filteredArticles}
        renderItem={({ item }) => (
          <PerspectiveTile
            cluster={cluster}
            perspective={item}
            readonly={true}
            onPress={() => props.onPerspectivePress(item)}
            navigateToContributions={(contributionType) =>
              props.navigateToContributions?.(item.id, contributionType)
            }
          />
        )}
        extraData={cluster.articleVotes}
        ListEmptyComponent={<NoPerspectivesFound />}
      />
    </MelddCard>
  )
}

const useStyle = createStyle(({ theme }) => ({
  titleWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing(1),
    paddingHorizontal: theme.spacing(4),
  },
  title: {
    flex: 1,
  },
  mainTitle: {
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'center',
  },
  button: {
    marginRight: -theme.spacing(1),
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  subTitle: {
    color: theme.colors.onSurfaceVariant,
    alignContent: 'center',
    flexDirection: 'row',
    columnGap: theme.spacing(1),
  },
  moderators: {
    flexDirection: 'row',
    columnGap: theme.spacing(2),
    marginTop: theme.spacing(1),
    overflow: 'hidden',
  },
  moderator: {
    flexShrink: 1,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
    paddingVertical: theme.spacing(3),
    paddingHorizontal: theme.spacing(4),
  },
}))
